import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import { discoverHub } from './hubDiscoveryService';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import { getLocalHubCredential } from './hubCredentialStore';
import {
  startHubControlChannel,
  stopHubControlChannel,
} from './hubControlChannelService';
import { pairWithHub } from './hubPairingService';
import { getConnectivityState } from '@/services/connectivity/connectivityService';

let runtimeStore = null;
let discoveryTimer = null;
let discoveryRunning = false;
let listenersRegistered = false;

function hasSession(store) {
  const value = store?.state?.partner_token || getPartnerToken();
  return value && value !== 'null' && value !== 'undefined';
}

function credentialNeedsAuthorizationRefresh(credential) {
  if (!credential) return false;
  if (Number(credential.grant_contract_version || 0) < 3) return true;
  const expiresAt = new Date(credential.offline_grant_expires_at || 0).getTime();
  return !Number.isFinite(expiresAt)
    || expiresAt <= Date.now() + (24 * 60 * 60 * 1000);
}

async function refreshOfflineAuthorization(discovery) {
  if (!discovery?.profile?.real_local_mode_enabled) {
    return getLocalHubCredential(discovery?.profile?.hub_uuid);
  }
  let credential = await getLocalHubCredential(discovery.profile.hub_uuid);
  if (
    !credentialNeedsAuthorizationRefresh(credential)
    || getConnectivityState().backendAvailable !== true
  ) {
    return credential;
  }
  try {
    credential = await pairWithHub(
      runtimeStore,
      discovery,
      runtimeStore.state.device.device?.name || 'Dispositivo EWU',
    );
  } catch (error) {
    console.warn('EWU offline authorization refresh failed:', error);
  }
  return credential;
}

export async function refreshLocalControlDiscovery({ force = false } = {}) {
  if (!OFFLINE_MODE_ENABLED) return null;
  if (discoveryRunning || !runtimeStore || !hasSession(runtimeStore)) return null;
  if (!force && runtimeStore.state.device.localControlState?.connected) {
    return refreshOfflineAuthorization(
      runtimeStore.state.device.localControlDiscovery,
    );
  }
  discoveryRunning = true;
  try {
    const discovery = await discoverHub(runtimeStore);
    if (!discovery?.profile?.hub_uuid || !discovery?.url) return discovery;
    let credential = await refreshOfflineAuthorization(discovery);
    runtimeStore.commit('device/setLocalControlDiscovery', {
      ...discovery,
      paired: Boolean(credential),
    });
    if (credential) {
      await startHubControlChannel(runtimeStore, discovery);
    }
    return discovery;
  } finally {
    discoveryRunning = false;
  }
}

export async function startLocalControlRuntime(store) {
  if (!OFFLINE_MODE_ENABLED) return null;
  runtimeStore = store;
  if (!discoveryTimer) {
    discoveryTimer = window.setInterval(() => {
      void refreshLocalControlDiscovery().catch(() => {});
    }, 30000);
  }
  if (!listenersRegistered) {
    const networkChanged = (force = false) => {
      void refreshLocalControlDiscovery({ force }).catch(() => {});
    };
    window.addEventListener('online', () => networkChanged(true));
    window.addEventListener('offline', () => networkChanged(true));
    window.addEventListener('focus', () => networkChanged(false));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') networkChanged(false);
    });
    listenersRegistered = true;
  }
  return refreshLocalControlDiscovery();
}

export function stopLocalControlRuntime() {
  if (discoveryTimer) window.clearInterval(discoveryTimer);
  discoveryTimer = null;
  discoveryRunning = false;
  stopHubControlChannel();
  runtimeStore = null;
}
